/**
 * The Automotive House motion rulebook.
 *
 * 1. Photos fall in once as they come into view: [data-tah-reveal].
 *    Siblings arrive one after another, like lights coming on along a wall.
 * 2. The dark moment: [data-tah-dark-moment]. As that section reaches the middle
 *    of the screen the room dims around it; scrolling on brings the lights back.
 *    Scroll-linked only, never plays by itself. One per page.
 * 3. Share: <tah-share> opens the native share sheet on phones, a small menu on
 *    desktop.
 * 6. The stage: [data-tah-stage]. One piece at a time, full screen, changing every
 *    few seconds while it is on screen; scrolling hangs the picture in a frame on
 *    the wall (--p from 0 to 1). The editor and reduced motion show it still.
 *
 * Everything degrades to a still page: no JavaScript, reduced motion and the theme
 * editor all show every element at rest.
 */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const inEditor = document.documentElement.classList.contains('shopify-design-mode');

  // Horizon scrolls inside .page-wrapper on desktop (990px and up) and the window below
  // that. Scroll events from .page-wrapper don't reach window, so listen on document in
  // the capture phase, which sees both, and read the offset from whichever is moving.
  const onPageScroll = (fn) => document.addEventListener('scroll', fn, { passive: true, capture: true });
  const pageScrollTop = () => Math.max(window.scrollY, document.querySelector('.page-wrapper')?.scrollTop || 0);

  /* ---- 1. Fall-in reveals ----------------------------------------------- */

  function setUpReveals(root = document) {
    if (reduceMotion || inEditor || !('IntersectionObserver' in window)) return;
    // Anything already on screen stays put; only what you scroll to falls in.
    const vh = window.innerHeight;
    const items = [...root.querySelectorAll('[data-tah-reveal]:not(.is-in)')].filter((el) => {
      if (el.getBoundingClientRect().top < vh) {
        el.classList.add('is-in');
        return false;
      }
      return true;
    });
    if (!items.length) return;
    document.documentElement.classList.add('tah-reveal-ready');

    // Stagger siblings that share a parent, 70 ms apart, capped so long grids don't drag.
    const byParent = new Map();
    for (const el of items) {
      const list = byParent.get(el.parentElement) || [];
      list.push(el);
      byParent.set(el.parentElement, list);
    }
    for (const list of byParent.values()) {
      list.forEach((el, i) => el.style.setProperty('--tah-delay', `${Math.min(i, 5) * 70}ms`));
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    items.forEach((el) => io.observe(el));
  }

  /* ---- 2. The dark moment ---------------------------------------------- */

  function setUpDarkMoment() {
    const section = document.querySelector('[data-tah-dark-moment]');
    if (!section || inEditor) return;

    let veil = document.querySelector('.tah-veil');
    if (!veil) {
      veil = document.createElement('div');
      veil.className = 'tah-veil';
      veil.setAttribute('aria-hidden', 'true');
      document.body.appendChild(veil);
    }

    const maxDim = Number(section.dataset.tahDarkness || 0.92);
    let frame = null;
    let last = -1;

    const update = () => {
      frame = null;
      const vh = window.innerHeight;
      const r = section.getBoundingClientRect();
      const off = Math.abs(r.top + r.height / 2 - vh / 2) / (vh * 0.85);
      let t = Math.min(1, Math.max(0, (1 - off - 0.12) / 0.5));
      t = t * t * (3 - 2 * t); // ease in and out of the dark
      if (Math.abs(t - last) < 0.005) return;
      last = t;
      veil.style.opacity = String(t * maxDim);
      // the header dims with the room (sections/tah-header.liquid)
      document.documentElement.style.setProperty('--tah-room-dim', (t * maxDim).toFixed(3));
      section.style.setProperty('--tah-dim', t.toFixed(3));
      section.classList.toggle('is-dark', t > 0.5);
    };
    const request = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    onPageScroll(request);
    window.addEventListener('resize', request);
    update();
  }

  /* ---- 3. Share --------------------------------------------------------- */

  class TahShare extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-share-toggle]');
      this.menu = this.querySelector('[data-share-menu]');
      this.copyButton = this.querySelector('[data-share-copy]');
      if (!this.button || !this.menu) return;

      this.button.addEventListener('click', () => this.toggle());
      this.copyButton?.addEventListener('click', () => this.copy());
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.close();
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.close();
          this.button.focus();
        }
      });
    }

    async toggle() {
      const coarse = window.matchMedia('(pointer: coarse)').matches;
      if (coarse && navigator.share) {
        try {
          await navigator.share({ title: this.dataset.title, url: this.dataset.url });
          return;
        } catch (e) {
          // Cancelled by the visitor: leave it closed. Refused: fall back to the menu.
          if (e && e.name === 'AbortError') return;
        }
      }
      const open = this.menu.hidden;
      this.menu.hidden = !open;
      this.button.setAttribute('aria-expanded', String(open));
    }

    close() {
      if (!this.menu || this.menu.hidden) return;
      this.menu.hidden = true;
      this.button.setAttribute('aria-expanded', 'false');
    }

    async copy() {
      const label = this.copyButton.querySelector('[data-share-copy-label]') || this.copyButton;
      try {
        await navigator.clipboard.writeText(this.dataset.url);
        label.textContent = this.copyButton.dataset.copied || 'Link copied';
      } catch {
        label.textContent = this.dataset.url;
      }
    }
  }
  if (!customElements.get('tah-share')) customElements.define('tah-share', TahShare);

  /* ---- 4. The menu ------------------------------------------------------- */

  class TahMenu extends HTMLElement {
    connectedCallback() {
      this.toggles = [...document.querySelectorAll('[data-tah-menu-toggle]')];
      this.imgs = [...this.querySelectorAll('[data-panel-img]')];
      this.caption = this.querySelector('[data-panel-caption]');
      this.capEyebrow = this.caption?.querySelector('[data-cap-eyebrow]');
      this.capLabel = this.caption?.querySelector('[data-cap-label]');
      this.capNote = this.caption?.querySelector('[data-cap-note]');
      this.capLink = this.caption?.querySelector('[data-cap-link]');
      this.first = this.querySelector('[data-panel]');
      // The photo panel only shows from 990px; below that no panel photo is ever fetched.
      this.wide = window.matchMedia('(min-width: 990px)');
      this.onKey = this.onKey.bind(this);

      this.toggles.forEach((t) => t.addEventListener('click', () => (this.isOpen ? this.close() : this.open())));
      this.querySelectorAll('[data-panel]').forEach((el) => {
        el.addEventListener('mouseenter', () => this.show(el));
        el.addEventListener('focus', () => this.show(el));
      });
      this.querySelectorAll('a[href]').forEach((a) => a.addEventListener('click', () => this.close(true)));

      // On phones the small groups start folded.
      if (window.matchMedia('(max-width: 989px)').matches) {
        this.querySelectorAll('details.tah-menu__group--small').forEach((d) => d.removeAttribute('open'));
      }
    }

    get isOpen() {
      return document.documentElement.classList.contains('tah-menu-open');
    }

    open() {
      if (!this.preloaded && this.wide.matches) {
        // Fetch every panel photo once, so hovering never waits for a download.
        // Only on open, and only where the panel shows (never on phones).
        this.preloaded = true;
        const srcs = new Set([...this.querySelectorAll('[data-panel-src]')].map((el) => el.dataset.panelSrc).filter(Boolean));
        srcs.forEach((src) => { const img = new Image(); img.src = src; });
      }
      this.hidden = false;
      document.documentElement.classList.add('tah-menu-open');
      this.toggles.forEach((t) => {
        t.setAttribute('aria-expanded', 'true');
        const label = t.querySelector('.tah-menu-toggle__label');
        if (label) label.textContent = label.dataset.close;
      });
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
      if (this.first) this.show(this.first, true);
      document.addEventListener('keydown', this.onKey);
      setTimeout(() => this.querySelector('.tah-menu__nav a, .tah-menu__nav summary')?.focus({ preventScroll: true }), 120);
    }

    close(navigating = false) {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      document.documentElement.classList.remove('tah-menu-open');
      this.toggles.forEach((t) => {
        t.setAttribute('aria-expanded', 'false');
        const label = t.querySelector('.tah-menu-toggle__label');
        if (label) label.textContent = label.dataset.open;
      });
      document.removeEventListener('keydown', this.onKey);
      setTimeout(() => { if (!this.isOpen) this.hidden = true; }, reduceMotion ? 0 : 360);
      if (!navigating) this.toggles[0]?.focus({ preventScroll: true });
    }

    onKey(e) {
      if (e.key === 'Escape') return this.close();
      if (e.key !== 'Tab') return;
      // Keep focus inside the menu and its toggle while it is open.
      const focusable = [this.toggles[0], ...this.querySelectorAll('a[href]:not([tabindex="-1"]), summary, button')].filter(
        (el) => el && el.offsetParent !== null && !el.closest('[aria-hidden="true"]')
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    // Cross-fades the panel to this photo; no photo (a department without its own)
    // fades the panel to the dark, leaving just the caption. The panel images carry
    // no src until the menu first asks for one here.
    showPhoto(src) {
      if (this.imgs.length < 2 || !this.wide.matches) return;
      this.wanted = src;
      const current = this.imgs.find((img) => img.classList.contains('is-on'));
      if (!src) {
        this.imgs.forEach((img) => img.classList.remove('is-on'));
        return;
      }
      if (current && current.getAttribute('src') === src) return;
      const next =
        this.imgs.find((img) => img !== current && img.getAttribute('src') === src) ||
        this.imgs.find((img) => img !== current);
      const reveal = () => {
        if (this.wanted !== src) return; // the pointer has moved on
        this.imgs.forEach((img) => img.classList.toggle('is-on', img === next));
      };
      if (next.getAttribute('src') === src && next.complete) reveal();
      else {
        next.onload = reveal;
        next.src = src;
      }
    }

    show(el, instant = false) {
      this.showPhoto(el.dataset.panelSrc || '');
      if (!this.caption || !this.capLabel) return;
      const apply = () => {
        this.capEyebrow.textContent = el.dataset.panelEyebrow || '';
        this.capLabel.textContent = el.dataset.panelLabel || '';
        this.capNote.textContent = el.dataset.panelNote || '';
        const href = el.dataset.panelHref;
        this.capLink.hidden = !href;
        if (href) this.capLink.href = href;
        this.caption.classList.remove('is-changing');
      };
      if (instant || reduceMotion) return apply();
      if (this.capLabel.textContent === (el.dataset.panelLabel || '')) return;
      this.caption.classList.add('is-changing');
      clearTimeout(this.captionTimer);
      this.captionTimer = setTimeout(apply, 180);
    }
  }
  if (!customElements.get('tah-menu')) customElements.define('tah-menu', TahMenu);

  /* ---- 6. Collection view: Gallery, 2 across, 4 across ----------------- */

  const VIEW_KEY = 'tah-grid-view';
  const storedView = () => {
    try { return localStorage.getItem(VIEW_KEY) || 'gallery'; } catch { return 'gallery'; }
  };
  const applyView = (root = document) => {
    /* Only where the view switch is offered (collection pages). Search, the
       home page and recommendations keep their own layouts. */
    if (!document.querySelector('tah-view-switch')) return;
    const view = storedView();
    root.querySelectorAll?.('.product-grid').forEach((g) => { g.dataset.tahView = view; });
    if (root.matches?.('.product-grid')) root.dataset.tahView = view;
  };

  /**
   * Right-sized grid photos. Horizon guesses one photo size for every card, but in
   * the Gallery view some cards are twice as wide, and the 2 across view makes all
   * of them wide. Once the grid is laid out, tell each photo its real width so the
   * browser fetches a sharp copy for large cards and keeps small cards light.
   * A photo already loaded sharp enough for its size is left alone, so the same
   * photo is never fetched twice (grid photos load lazily with sizes="auto", which
   * most browsers size correctly on their own; this is the fallback).
   */
  const loadedWidth = (img) => Number(/[?&]width=(\d+)/.exec(img.currentSrc || '')?.[1] || 0);
  const rightSizePhotos = (root = document) => {
    const imgs = root.querySelectorAll?.('.product-grid img[srcset]') || [];
    const dpr = window.devicePixelRatio || 1;
    imgs.forEach((img) => {
      const w = Math.round(img.getBoundingClientRect().width);
      if (w <= 0 || img.getAttribute('sizes') === `${w}px`) return;
      if (img.complete && loadedWidth(img) >= w * dpr * 0.9) return;
      img.setAttribute('sizes', `${w}px`);
    });
  };
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => rightSizePhotos(), 200);
  });

  class TahViewSwitch extends HTMLElement {
    connectedCallback() {
      this.buttons = [...this.querySelectorAll('[data-view]')];
      this.buttons.forEach((b) => b.addEventListener('click', () => this.pick(b.dataset.view)));
      this.mark(storedView());
    }
    pick(view) {
      try { localStorage.setItem(VIEW_KEY, view); } catch { /* private mode: still switch for this page */ }
      this.mark(view);
      document.querySelectorAll('.product-grid').forEach((g) => { g.dataset.tahView = view; });
      requestAnimationFrame(() => rightSizePhotos());
      if (!reduceMotion) {
        document.querySelectorAll('.product-grid .product-grid__item').forEach((el, i) => {
          el.animate([{ opacity: 0.2, transform: 'translateY(10px)' }, { opacity: 1, transform: 'none' }], {
            duration: 500, delay: Math.min(i, 12) * 30, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)', fill: 'backwards',
          });
        });
      }
    }
    mark(view) {
      this.buttons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.view === view)));
    }
  }
  if (!customElements.get('tah-view-switch')) customElements.define('tah-view-switch', TahViewSwitch);

  /* ---- 5. Header slims a little once you scroll ------------------------- */

  function setUpHeader() {
    const header = document.querySelector('[data-tah-header]');
    if (!header) return;
    const publish = () => {
      const h = header.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--tah-header-h', `${h}px`);
      document.body.style.setProperty('--header-height', `${h}px`);
    };
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        header.classList.toggle('is-condensed', pageScrollTop() > 16);
        publish();
      });
    };
    onPageScroll(onScroll);
    window.addEventListener('resize', publish);
    header.addEventListener('transitionend', publish);
    onScroll();
  }

  /* ---- 6. The stage ---------------------------------------------------- */

  function setUpStage() {
    const stage = document.querySelector('[data-tah-stage]');
    if (!stage) return;
    const sticky = stage.querySelector('.tah-stage__sticky');
    const slides = [...stage.querySelectorAll('.tah-stage__slide')];
    const tabs = [...stage.querySelectorAll('[data-stage-tab]')];
    const label = stage.querySelector('[data-stage-label]');
    const dwell = Number(stage.dataset.dwell || 7) * 1000;
    let current = 0;
    let timer = null;
    let onScreen = true;

    const warm = (n) => {
      const img = slides[n]?.querySelector('img');
      if (img && img.loading === 'lazy') img.loading = 'eager';
    };
    const show = (n) => {
      if (!slides.length) return;
      current = (n + slides.length) % slides.length;
      slides.forEach((s, k) => s.classList.toggle('is-on', k === current));
      tabs.forEach((t, k) => {
        t.classList.toggle('is-on', k === current);
        t.setAttribute('aria-current', k === current ? 'true' : 'false');
      });
      const s = slides[current];
      if (label) {
        label.href = s.dataset.link || label.href;
        label.querySelector('b').textContent = s.dataset.title || '';
        label.querySelector('span').textContent = s.dataset.caption || '';
      }
      warm((current + 1) % slides.length);
    };
    const play = () => {
      clearInterval(timer);
      const still = reduceMotion || inEditor || slides.length < 2;
      stage.classList.toggle('is-playing', !still);
      if (still) return;
      timer = setInterval(() => {
        if (onScreen && !document.hidden) show(current + 1);
      }, dwell);
    };
    tabs.forEach((t) =>
      t.addEventListener('click', () => {
        show(Number(t.dataset.stageTab));
        // restart the running line on the newly chosen piece
        stage.classList.remove('is-playing');
        void stage.offsetWidth;
        play();
      })
    );
    // A film plays only while it is on screen. It starts by itself only when motion is
    // allowed and the visitor has not asked to save data; otherwise its poster stays
    // until they press Play. The markup has no autoplay and preloads nothing.
    const film = stage.querySelector('video.tah-stage__video');
    const pauseButton = stage.querySelector('[data-stage-pause]');
    let wantPlay = !!film && !reduceMotion && !navigator.connection?.saveData;
    const syncPause = () => {
      if (!film || !pauseButton) return;
      const paused = film.paused;
      pauseButton.setAttribute('aria-pressed', paused ? 'true' : 'false');
      pauseButton.querySelector('span').textContent = paused ? 'Play' : 'Pause';
    };
    const playFilm = () => {
      wantPlay = true;
      film.play().catch(syncPause);
    };
    if (film) {
      film.addEventListener('play', syncPause);
      film.addEventListener('pause', syncPause);
      if (pauseButton) {
        if (!wantPlay) syncPause(); // the markup says Pause, for a film about to play
        pauseButton.hidden = false;
        pauseButton.addEventListener('click', () => {
          if (film.paused) playFilm();
          else {
            wantPlay = false;
            film.pause();
          }
        });
      }
    }
    new IntersectionObserver(([e]) => {
      onScreen = e.isIntersecting;
      if (!film) return;
      if (!onScreen) film.pause();
      else if (wantPlay) playFilm();
    }).observe(sticky);

    // Sound on / off: the music fades in and out rather than switching abruptly.
    // A plain timer (not animation frames) so the fade also completes in a background tab.
    const soundButton = stage.querySelector('[data-stage-sound]');
    if (film && soundButton) {
      let soundOn = false;
      let ramp = null;
      const fadeTo = (target, ms, done) => {
        clearInterval(ramp);
        const from = film.volume;
        const t0 = performance.now();
        ramp = setInterval(() => {
          const k = Math.min(1, (performance.now() - t0) / ms);
          film.volume = from + (target - from) * k;
          if (k >= 1) {
            clearInterval(ramp);
            if (done) done();
          }
        }, 30);
      };
      soundButton.addEventListener('click', () => {
        soundOn = !soundOn;
        if (soundOn) {
          film.volume = 0;
          film.muted = false;
          if (film.paused) playFilm();
          fadeTo(1, 1200);
        } else {
          fadeTo(0, 600, () => (film.muted = true));
        }
        soundButton.setAttribute('aria-pressed', soundOn ? 'true' : 'false');
        soundButton.querySelector('span').textContent = soundOn ? 'Sound off' : 'Sound on';
      });
    }

    // In the editor, selecting a piece shows it.
    document.addEventListener('shopify:block:select', (e) => {
      const k = slides.indexOf(e.target.closest?.('.tah-stage__slide'));
      if (k > -1) show(k);
    });

    // Scrolling hangs the picture on the wall.
    if (!reduceMotion && !inEditor) {
      let frame = null;
      let last = -1;
      const update = () => {
        frame = null;
        const run = stage.offsetHeight - sticky.offsetHeight;
        let p = run > 0 ? Math.min(1, Math.max(0, -stage.getBoundingClientRect().top / run)) : 0;
        p = p * p * (3 - 2 * p);
        if (Math.abs(p - last) < 0.002) return;
        last = p;
        stage.style.setProperty('--p', p.toFixed(3));
        // The film controls hide while the picture moves, then sit under the frame.
        stage.classList.toggle('is-past-words', p > 0.42);
        stage.classList.toggle('is-labelled', p > 0.7);
        stage.classList.toggle('is-hung', p > 0.85);
      };
      const request = () => {
        if (!frame) frame = requestAnimationFrame(update);
      };
      onPageScroll(request);
      window.addEventListener('resize', request);
      update();
    }

    show(0);
    play();
  }

  /* ---- Product page: a tall details column sticks by its bottom ---------- */
  // Title, price, buttons, description and the spec list are often taller than a
  // laptop screen. Stuck by its top, the spec list stayed hidden until the photos
  // ran out. The column's height goes to CSS (--tah-details-h), which lets a tall
  // column scroll until its bottom shows, then stick (tah-brand.css).
  function setUpStickyDetails() {
    const col = document.querySelector('.product-details.sticky-content--desktop');
    if (!col || !('ResizeObserver' in window)) return;
    const set = () => col.style.setProperty('--tah-details-h', `${col.offsetHeight}px`);
    new ResizeObserver(set).observe(col);
    set();
  }

  /* ---- Boot ------------------------------------------------------------- */

  const boot = () => {
    applyView();
    requestAnimationFrame(() => rightSizePhotos());
    setUpHeader();
    setUpReveals();
    setUpDarkMoment();
    setUpStage();
    setUpStickyDetails();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Horizon re-renders the product grid when filters change; reveal the new cards too.
  document.addEventListener('shopify:section:load', (e) => setUpReveals(e.target));
  new MutationObserver((records) => {
    for (const r of records) {
      for (const n of r.addedNodes) {
        if (!(n instanceof HTMLElement)) continue;
        setUpReveals(n);
        applyView(n); // Horizon swaps the grid when filters change; keep the chosen view.
        if (n.querySelector?.('.product-grid img[srcset]') || n.matches?.('.product-grid__item')) {
          requestAnimationFrame(() => rightSizePhotos(n));
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
})();
